
import React, { useState, useEffect, useCallback } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import { debounce } from 'lodash';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Tooltip,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Apps as AppsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  PersonAdd as PersonAddIcon,
  Groups as GroupsIcon,
  Block as BlockIcon,
  CheckCircle,
  Refresh as RefreshIcon,
  
} from '@mui/icons-material';
import InfoIcon from '@mui/icons-material/Info';
import StorageIcon from '@mui/icons-material/Storage';
import { useNavigate } from 'react-router-dom';
import DataTable from '../common/DataTable';
import LoadingSpinner from '../common/LoadingSpinner';
import { adminService } from '../../services/admin';
import { BUSINESS_STATUS, BUSINESS_STATUS_COLORS } from '../../utils/constants';
import BusinessDomainsDialog from './BusinessDomainsDialog';
import PublicIcon from '@mui/icons-material/Public';
import KeyIcon from '@mui/icons-material/Key';
const BusinessList = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [blockDialog, setBlockDialog] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  // 2. Add state
const [allowlistDialog, setAllowlistDialog] = useState(null);
const [publicKeyDialog, setPublicKeyDialog] = useState(null);
  // 🔐 Store revealed secrets per business
  const [revealedSecrets, setRevealedSecrets] = useState({});

  const [metadataDialog, setMetadataDialog] = useState(null); // holds the business object
  const [metadataInput, setMetadataInput] = useState('');
  const [metadataLoading, setMetadataLoading] = useState(false);

  // const [merchantKey, setMerchantKey] = useState('');
  const [merchantName, setMerchantName] = useState('');
  const [merchantLogo, setMerchantLogo] = useState('');
  const [merchantPrimaryColor, setMerchantPrimaryColor] = useState('');
  const [merchantTheme, setMerchantTheme] = useState('');
  const [merchantPublicKey, setMerchantPublicKey] = useState('');
  const [merchantRedirectUrl, setMerchantRedirectUrl] = useState('');

  const [viewMetadataDialog, setViewMetadataDialog] = useState(null);
  const [viewMetadataData, setViewMetadataData] = useState(null);

  const [confirmDelete, setConfirmDelete] = useState(null); // holds domain string
  
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearch(value);
      setPage(0);
    }, 500),
    []
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  // Initial load only
  useEffect(() => {
    fetchBusinesses(true);
  }, []);

  // Effect for filter/pagination changes - FIXED: removed initialLoading from dependencies
  useEffect(() => {
    // Only fetch if not in initial loading state
    if (!initialLoading) {
      fetchBusinesses(false);
    }
  }, [page, rowsPerPage, search, filterStatus]); // Removed initialLoading from here

  const fetchBusinesses = async (isInitial = false) => {
    try {
      if (isInitial) {
        setInitialLoading(true);
      } else {
        setTableLoading(true);
      }
      
      const res = await adminService.getAllBusinesses(
        page,
        rowsPerPage,
        search,
        filterStatus !== 'ALL' ? filterStatus : ''
      );
      
      // Transform the data to ensure blocked status is properly reflected
      const transformedData = (res.data || []).map(business => ({
        ...business,
        status: business.isBlocked ? 'BLOCKED' : (business.status || 'ACTIVE')
      }));
      
      setBusinesses(transformedData);
      setTotalRows(res.total || 0);
    } catch (error) {
      console.error('Error fetching businesses:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch businesses',
        severity: 'error'
      });
    } finally {
      if (isInitial) {
        setInitialLoading(false);
      }
      setTableLoading(false); // Always set table loading to false
    }
  };

  const handleRefresh = () => {
    fetchBusinesses(false);
    setSnackbar({
      open: true,
      message: 'Business list refreshed',
      severity: 'success'
    });
  };

  // 🔐 Toggle secret reveal
  const handleToggleSecret = async (business) => {
    const id = business._id;

    // If already revealed → hide it
    if (revealedSecrets[id]) {
      setRevealedSecrets(prev => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
      return;
    }

    try {
      const res = await adminService.getBusinessSecret(id);
      setRevealedSecrets(prev => ({
        ...prev,
        [id]: res.secretKey
      }));
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to fetch secret key',
        severity: 'error'
      });
    }
  };

  // Handle block action
  const handleBlockClick = (business) => {
    setBlockDialog({
      business,
      action: 'block'
    });
    setBlockReason('');
  };

  // Handle unblock action
  const handleUnblockClick = (business) => {
    setBlockDialog({
      business,
      action: 'unblock'
    });
    setBlockReason(''); // Clear previous reason 
  };
  // Metadata handlers

const handleMetadataClick = (business) => {
  setMetadataDialog(business);
  

  setMerchantName('');
  setMerchantLogo('');
  setMerchantPrimaryColor('');
  setMerchantTheme('');
  setMerchantPublicKey('');
  setMerchantRedirectUrl('');

  // Pre-fill if metadata exists
 if (business.metadata && Object.keys(business.metadata).length > 0) {
  const firstKey = Object.keys(business.metadata)[0];
  // Handle both flat { name, logo, ... } and nested { key: { name, logo, ... } }
  const raw = business.metadata[firstKey];
  const data = (raw && typeof raw === 'object' && raw.name !== undefined)
    ? raw               // nested structure
    : business.metadata; // flat structure ← this is what your submit saves

  setMerchantName(data.name || '');
  setMerchantLogo(data.logo || '');
  setMerchantPrimaryColor(data.primaryColor || '');
  setMerchantTheme(data.theme || '');
  setMerchantPublicKey(data.publicKey || '');
  setMerchantRedirectUrl(
    Array.isArray(data.redirectUrl) ? data.redirectUrl.join(', ') : (data.redirectUrl || '')
  );
} else {
  // Pre-fill sensible defaults for new metadata
  setMerchantName(business.businessName || '');
  setMerchantLogo('');
  setMerchantPrimaryColor('#667eea');
  setMerchantTheme('light');
  setMerchantPublicKey('');
  setMerchantRedirectUrl('');
}

};

const handleMetadataSubmit = async () => {
  if (!metadataDialog) return;

  // Validation
  
  if (!merchantName.trim()) {
    setSnackbar({ open: true, message: 'Merchant Name is required', severity: 'error' });
    return;
  }

  
  // Build metadata object (flat)
  const metadata = {
    name: merchantName,
    logo: merchantLogo,
    primaryColor: merchantPrimaryColor,
    theme: merchantTheme,
    publicKey: merchantPublicKey,
    redirectUrl: merchantRedirectUrl.split(',').map(s => s.trim()).filter(s => s),
  };

  try {
    setMetadataLoading(true);
    // Use tenantId (custom businessId) – this should be correct
    await adminService.updateBusinessMetadata(metadataDialog._id, metadata);
    setSnackbar({ open: true, message: 'Metadata updated successfully', severity: 'success' });
    setMetadataDialog(null);
    await fetchBusinesses(false);
  } catch (error) {
    setSnackbar({
      open: true,
      message: error.response?.data?.message || 'Failed to update metadata',
      severity: 'error'
    });
  } finally {
    setMetadataLoading(false);
  }
};

const handleViewMetadataClick = (business) => {
  setViewMetadataDialog(business);
  setViewMetadataData(business.metadata || {});
};

  const handleBlockUnblockConfirm = async () => {
  const { business, action } = blockDialog;
  
  try {
    setActionLoading(true);
    
    if (action === 'block') {
      await adminService.blockBusiness(business._id, blockReason);
      setSnackbar({
        open: true,
        message: 'Business blocked successfully',
        severity: 'success'
      });
    } else {
      // Pass reason for unblock as well
      await adminService.unblockBusiness(business._id, blockReason);
      setSnackbar({
        open: true,
        message: 'Business unblocked successfully',
        severity: 'success'
      });
    }
    
    // Refresh only the table data
    await fetchBusinesses(false);
    setBlockDialog(null);
    setBlockReason('');
    
  } catch (error) {
    console.error(`Failed to ${action} business:`, error);
    setSnackbar({
      open: true,
      message: error.response?.data?.message || `Failed to ${action} business`,
      severity: 'error'
    });
  } finally {
    setActionLoading(false);
  }
};
  // Handle delete
const handleDelete = async () => {
  if (!deleteDialog) return;
  try {
    setActionLoading(true);
    await adminService.deleteBusiness(deleteDialog._id);
    setSnackbar({
      open: true,
      message: 'Business deleted successfully',
      severity: 'success'
    });
    setDeleteDialog(null);
    await fetchBusinesses(false);
  } catch (error) {
    console.error('Failed to delete business:', error);
    setSnackbar({
      open: true,
      message: error.message || 'Failed to delete business',
      severity: 'error'
    });
  } finally {
    setActionLoading(false);
  }
};

  const columns = [
    { field: 'businessName', headerName: 'Business Name', sortable: true },
    { field: 'industry', headerName: 'Industry', sortable: true },
    { field: 'contactEmail', headerName: 'Contact Email', sortable: true },
{
  field: 'businessId',
  headerName: 'Business ID',
  width: 220,
  sortable: true,
  render: (value) => {
    const fullId = value || '';
    const displayId = fullId.length > 16 ? `${fullId.substring(0, 14)}…` : fullId;

    const handleCopy = () => {
      if (fullId) {
        navigator.clipboard.writeText(fullId)
          .then(() => {
            // Optional: show a snackbar or toast
            console.log('Copied:', fullId);
          })
          .catch(err => console.error('Copy failed:', err));
      }
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title={`Click to copy: ${fullId}`}>
          <Typography
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              cursor: 'pointer',
              '&:hover': { color: '#3182ce' },
            }}
            onClick={handleCopy}
          >
            {displayId}
          </Typography>
        </Tooltip>
        <Tooltip title="Copy full Business ID">
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{ p: 0.5, color: '#64748b', '&:hover': { color: '#3182ce' } }}
          >
            <ContentCopyIcon fontSize="small" /> {/* import ContentCopyIcon from '@mui/icons-material/ContentCopy' */}
          </IconButton>
        </Tooltip>
      </Box>
    );
  },
},
{
  field: 'hasMetadata',
  headerName: 'Public Key',
  width: 200,
  render: (_, business) => {
    const publicKey = business.metadata?.publicKey;

    if (!publicKey) {
      return (
        <Chip
          label="+ Add"
          size="small"
          onClick={() => handleMetadataClick(business)}
          sx={{
            height: 22, fontSize: '0.7rem', fontWeight: 600,
            bgcolor: alpha(theme.palette.success.main, isDark ? 0.22 : 0.1),
            color: theme.palette.success.main,
            border: `1px solid ${alpha(theme.palette.success.main, isDark ? 0.4 : 0.25)}`,
            cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.success.main, isDark ? 0.32 : 0.18) },
          }}
        />
      );
    }

    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* View button – opens the public key dialog */}
        <Chip
          label="View"
          size="small"
          icon={<InfoIcon sx={{ fontSize: '13px !important' }} />}
          onClick={() => setPublicKeyDialog(business)}
          sx={{
            height: 22, fontSize: '0.7rem', fontWeight: 600,
            bgcolor: alpha(theme.palette.info.main, isDark ? 0.22 : 0.1),
            color: theme.palette.info.main,
            border: `1px solid ${alpha(theme.palette.info.main, isDark ? 0.4 : 0.25)}`,
            cursor: 'pointer',
            '&:hover': { bgcolor: alpha(theme.palette.info.main, isDark ? 0.32 : 0.18) },
          }}
        />

        {/* Edit button – opens the edit dialog (only public key) */}
        <Chip
          label="Edit"
          size="small"
          icon={<StorageIcon sx={{ fontSize: '13px !important' }} />}
          onClick={() => handleMetadataClick(business)}
          sx={{
            height: 22, fontSize: '0.7rem', fontWeight: 600,
            bgcolor: alpha(theme.palette.secondary.main, isDark ? 0.22 : 0.1),
            color: theme.palette.secondary.main,
            border: `1px solid ${alpha(theme.palette.secondary.main, isDark ? 0.4 : 0.25)}`,
            cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.secondary.main, isDark ? 0.32 : 0.18) },
          }}
        />
      </Box>
    );
  },
},

    // 🔐 Secret Key Column (inline reveal)
    {
      field: 'secretKey',
      headerName: 'Secret Key',
      render: (_, business) => {
        const secret = revealedSecrets[business._id];

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontFamily: 'monospace' }}>
              {secret ? secret : '***************'}
            </Typography>

            <Tooltip title={secret ? "Hide Secret" : "View Secret"}>
              <IconButton
                size="small"
                onClick={() => handleToggleSecret(business)}
              >
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },

    {
      field: 'status',
      headerName: 'Status',
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          size="small"
          color={BUSINESS_STATUS_COLORS[value] || 'default'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      align: 'center',
      render: (_, business) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        
          <Tooltip title="View Domains">
            <IconButton
              size="small"
              onClick={() => setAllowlistDialog(business)}
              sx={{ color: '#0369a1', '&:hover': { backgroundColor: '#e0f2fe' } }}
            >
              <PublicIcon fontSize="small" />  {/* import PublicIcon from '@mui/icons-material/Public' */}
            </IconButton>
          </Tooltip>
          <Tooltip title="View Users">
            <IconButton
              size="small"
              onClick={() =>
                navigate(`/admin/businesses/${business.businessId}/users`)
              }
              sx={{ color: '#7c3aed', '&:hover': { backgroundColor: '#ede9fe' } }}
            >
              <GroupsIcon fontSize="small" />
            </IconButton>
          </Tooltip>

       
         <Tooltip title="Edit Business">
          <IconButton
            size="small"
            onClick={() => navigate(`/admin/businesses/${business._id}/edit`)}
            sx={{ color: '#f59e0b', '&:hover': { backgroundColor: '#fef3c7' } }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>

          <Tooltip title="Delete Business">
            <IconButton
              size="small"
              onClick={() => setDeleteDialog(business)}
              sx={{ color: '#ef4444', '&:hover': { backgroundColor: '#fee2e2' } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {business.status === 'BLOCKED' ? (
            <Tooltip title="Unblock Business">
              <IconButton
                size="small"
                onClick={() => handleUnblockClick(business)}
                sx={{ 
                  color: '#10b981',
                  '&:hover': { backgroundColor: '#ecfdf5' } 
                }}
              >
                <CheckCircle fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Block Business">
              <IconButton
                size="small"
                onClick={() => handleBlockClick(business)}
                sx={{ 
                  color: '#ef4444',
                  '&:hover': { backgroundColor: '#fee2e2' } 
                }}
              >
                <BlockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  // Show initial loading spinner only for first load
  if (initialLoading) return <LoadingSpinner />;

  return (
    <Box
      sx={{
        minHeight: '100%',
        background: theme.palette.background.default,
        p: 3,
      }}
    >
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            Businesses
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Manage registered businesses and applications
          </Typography>
        </Box>

        {/* Refresh Icon */}
        <Tooltip title="Refresh Table">
          <IconButton
            onClick={handleRefresh}
            disabled={tableLoading}
            sx={{
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.1)',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
              animation: tableLoading ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': {
                  transform: 'rotate(0deg)',
                },
                '100%': {
                  transform: 'rotate(360deg)',
                },
              },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.paper,
          boxShadow: isDark ? '0 1px 3px rgba(0, 0, 0, 0.4)' : '0 1px 3px rgba(15, 23, 42, 0.08)',
          p: 3,
        }}
      >
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search businesses"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                debouncedSearch(e.target.value);
              }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1 }} />,
              }}
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            sx={{ display: 'flex', justifyContent: 'flex-end' }}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/admin/businesses/create')}
              sx={{
                background:
                  '#2563EB',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 6px 16px rgba(102,126,234,0.35)',
              }}
            >
              Add Business
            </Button>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {[
            { value: 'ALL', label: 'ALL', color: theme.palette.primary.main },
            { value: 'ACTIVE', label: 'ACTIVE', color: theme.palette.success.main },
            { value: 'INACTIVE', label: 'INACTIVE', color: theme.palette.warning.main },
            { value: 'BLOCKED', label: 'BLOCKED', color: theme.palette.error.main },
          ].map((opt) => {
            const selected = filterStatus === opt.value;
            return (
              <Chip
                key={opt.value}
                label={opt.label}
                clickable
                onClick={() => setFilterStatus(opt.value)}
                sx={{
                  fontWeight: 600,
                  backgroundColor: selected ? alpha(opt.color, isDark ? 0.22 : 0.12) : 'transparent',
                  color: selected ? opt.color : theme.palette.text.secondary,
                  border: `1px solid ${selected ? opt.color : theme.palette.divider}`,
                }}
              />
            );
          })}
        </Box>

        {/* Table with its own loading state */}
        <Box sx={{ position: 'relative' }}>
          {tableLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isDark ? 'rgba(17, 24, 39, 0.7)' : 'rgba(255, 255, 255, 0.7)',
                zIndex: 1,
                borderRadius: 1,
              }}
            >
              <LoadingSpinner size={40} />
            </Box>
          )}
          
          <DataTable
            columns={columns}
            data={businesses}
            page={page}
            rowsPerPage={rowsPerPage}
            totalRows={totalRows}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
          />
        </Box>
      </Paper>

      {/* Delete Dialog */}
      <Dialog open={!!deleteDialog} onClose={() => !actionLoading && setDeleteDialog(null)}>
        <DialogTitle>Delete Business</DialogTitle>
        <DialogContent>
          Are you sure you want to delete{' '}
          <strong>{deleteDialog?.businessName}</strong>? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button 
            color="error" 
            onClick={handleDelete}
            disabled={actionLoading}
          >
            {actionLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>


          {allowlistDialog && (
  <BusinessDomainsDialog
    business={allowlistDialog}
    onClose={() => setAllowlistDialog(null)}
  />
)}
{/* Public Key Dialog */}
<Dialog
  open={!!publicKeyDialog}
  onClose={() => setPublicKeyDialog(null)}
  maxWidth="sm"
  fullWidth
>
  <DialogTitle>
    Public Key for {publicKeyDialog?.businessName}
  </DialogTitle>
  <DialogContent dividers>
    <Box
      sx={{
        bgcolor: '#0f172a',
        color: '#e2e8f0',
        p: 2,
        borderRadius: 1,
        fontFamily: 'monospace',
        fontSize: '0.8rem',
        overflow: 'auto',
        maxHeight: 300,
      }}
    >
      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {publicKeyDialog?.metadata?.publicKey || 'No public key available'}
      </pre>
    </Box>
  </DialogContent>
  <DialogActions>
    <Button
      onClick={() => {
        const key = publicKeyDialog?.metadata?.publicKey;
        if (key) {
          navigator.clipboard.writeText(key).catch(() => {});
          setSnackbar({
            open: true,
            message: 'Public key copied to clipboard',
            severity: 'success',
          });
        }
      }}
      startIcon={<ContentCopyIcon />}
    >
      Copy
    </Button>
    <Button onClick={() => setPublicKeyDialog(null)}>Close</Button>
  </DialogActions>
</Dialog>

  {/* Edit Public Key Dialog */}
<Dialog
  open={!!metadataDialog}
  onClose={() => !metadataLoading && setMetadataDialog(null)}
  maxWidth="sm"
  fullWidth
  PaperProps={{ sx: { maxWidth: '500px' } }}
>
  <DialogTitle sx={{ pb: 0.9 }}>
    Edit Public Key for {metadataDialog?.businessName}
  </DialogTitle>
  <DialogContent sx={{ pt: 1 }}>
    <TextField
      fullWidth
      value={merchantPublicKey}
      onChange={(e) => setMerchantPublicKey(e.target.value)}
      placeholder="Enter Public Key"
      disabled={metadataLoading}
      multiline
      rows={3}
      minRows={3}
      sx={{ mt: 0 }}
      InputProps={{ sx: { fontSize: '0.85rem' } }}
    />
  </DialogContent>
  <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
    <Button onClick={() => setMetadataDialog(null)} disabled={metadataLoading}>
      Cancel
    </Button>
    <Button
      onClick={handleMetadataSubmit}
      variant="contained"
      disabled={metadataLoading}
      sx={{
        background: '#2563EB',
      }}
    >
      {metadataLoading ? 'Saving...' : 'Save'}
    </Button>
  </DialogActions>
</Dialog>

    {/* View Metadata Dialog */}
<Dialog
  open={!!viewMetadataDialog}
  onClose={() => setViewMetadataDialog(null)}
  maxWidth="md"
  fullWidth
>
  <DialogTitle>
    Metadata for {viewMetadataDialog?.businessName}
  </DialogTitle>
  <DialogContent dividers>
    {viewMetadataData && Object.keys(viewMetadataData).length > 0 ? (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f5f5f5',
          maxHeight: '400px',
          overflow: 'auto',
          fontFamily: 'monospace',
          fontSize: '0.85rem'
        }}
      >
        <pre style={{ margin: 0 }}>
          {JSON.stringify(viewMetadataData, null, 2)}
        </pre>
      </Paper>
    ) : (
      <Alert severity="info">No metadata available for this business.</Alert>
    )}
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setViewMetadataDialog(null)}>Close</Button>
  </DialogActions>
</Dialog>
<Dialog 
  open={!!blockDialog} 
  onClose={() => !actionLoading && setBlockDialog(null)}
  maxWidth="sm"
  fullWidth
>
  <DialogTitle>
    {blockDialog?.action === 'block' ? 'Block Business' : 'Unblock Business'}
  </DialogTitle>
  <DialogContent>
    <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
      Are you sure you want to {blockDialog?.action} <strong>{blockDialog?.business?.businessName}</strong>?
    </Typography>
    
    <TextField
      autoFocus
      label={blockDialog?.action === 'block' ? 'Block Reason' : 'Unblock Reason'}
      fullWidth
      multiline
      rows={3}
      value={blockReason}
      onChange={(e) => setBlockReason(e.target.value)}
      placeholder={
        blockDialog?.action === 'block'
          ? 'Please provide a reason for blocking...'
          : 'Please provide a reason for unblocking...'
      }
      disabled={actionLoading}
      required
      error={blockReason.trim() === ''}
      helperText={blockReason.trim() === '' ? 'Reason is required' : ''}
    />
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setBlockDialog(null)} disabled={actionLoading}>
      Cancel
    </Button>
    <Button
      onClick={handleBlockUnblockConfirm}
      color={blockDialog?.action === 'block' ? 'error' : 'primary'}
      variant="contained"
      disabled={actionLoading || !blockReason.trim()}
    >
      {actionLoading ? 'Processing...' : blockDialog?.action === 'block' ? 'Block' : 'Unblock'}
    </Button>
  </DialogActions>
</Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BusinessList;